import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import md5 from "js-md5";
import { API } from "../lib/apiClient";
import { useI18n } from "../i18n/I18nContext";
import { LANGS } from "../i18n/translations";
import { Wifi, Loader2, CheckCircle2, XCircle } from "lucide-react";

function hexToStr(hex) {
  let s = "";
  for (let i = 0; i < hex.length; i += 2) s += String.fromCharCode(parseInt(hex.substr(i, 2), 16));
  return s;
}

function postTo(action, fields) {
  const f = document.createElement("form");
  f.method = "POST";
  f.action = action;
  Object.entries(fields).forEach(([k, v]) => {
    const i = document.createElement("input");
    i.type = "hidden"; i.name = k; i.value = v == null ? "" : v;
    f.appendChild(i);
  });
  document.body.appendChild(f);
  f.submit();
}

export default function GuestPortal() {
  const { structureId } = useParams();
  const [params] = useSearchParams();
  const { t, lang, changeLang } = useI18n();
  const [data, setData] = useState(null);
  const [code, setCode] = useState("");
  const [result, setResult] = useState(null); // {result, reason, guest_name}
  const [loading, setLoading] = useState(false);

  // MikroTik hotspot redirect parameters (present only on a real captive portal)
  const linkLoginOnly = params.get("link-login-only") || params.get("linkloginonly") || "";
  const linkOrig = params.get("link-orig") || params.get("dst") || "http://www.google.com";
  const mac = params.get("mac") || "";
  const chapId = params.get("chap-id") || "";
  const chapChallenge = params.get("chap-challenge") || "";
  const isRealHotspot = !!linkLoginOnly;

  useEffect(() => {
    axios.get(`${API}/public/portal/${structureId}`).then((r) => setData(r.data)).catch(() => setData(false));
  }, [structureId]);

  const connect = async () => {
    if (!code.trim()) return;
    setLoading(true);
    setResult(null);
    try {
      const { data: res } = await axios.post(`${API}/public/portal/authenticate`, { structure_id: structureId, code, mac });
      setResult(res);
      if (res.result === "accept" && isRealHotspot) {
        // Complete the login against the MikroTik hotspot servlet (browser -> router)
        const username = res.username || code;
        let password = res.password || code;
        if (chapId && chapChallenge) {
          // http-chap: password = MD5(chapId + plaintext + chapChallenge)
          password = md5(hexToStr(chapId) + (res.password || code) + hexToStr(chapChallenge));
        }
        setTimeout(() => postTo(linkLoginOnly, { username, password, dst: linkOrig, popup: "true" }), 600);
      }
    } catch (e) {
      setResult({ result: "reject", reason: "Errore" });
    } finally {
      setLoading(false);
    }
  };

  if (data === false) return <div className="min-h-screen flex items-center justify-center text-slate-400">404</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  const cfg = data.config;
  const color = cfg.primary_color || "#2563EB";
  const welcome = cfg[`welcome_${lang}`] || cfg.welcome_it;

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: "#0B0F17" }}>
      <div className="absolute top-4 right-4 flex gap-1">
        {LANGS.map((l) => (
          <button key={l.code} data-testid={`guest-lang-${l.code}`} onClick={() => changeLang(l.code)}
            className={`px-2.5 py-1 rounded-md text-xs font-semibold font-mono transition-colors ${lang === l.code ? "text-white" : "text-slate-500 hover:text-slate-200"}`}
            style={lang === l.code ? { background: color } : {}}>
            {l.flag}
          </button>
        ))}
      </div>

      <div data-testid="guest-portal" className="w-full max-w-md rounded-3xl overflow-hidden shadow-2xl" style={{ background: "#0d121c" }}>
        <div className="h-2" style={{ background: color }} />
        <div className="p-8 text-center">
          {cfg.logo_url ? (
            <img src={cfg.logo_url} alt="logo" className="h-16 mx-auto mb-5 object-contain" />
          ) : (
            <div className="h-16 w-16 rounded-2xl mx-auto mb-5 flex items-center justify-center" style={{ background: color + "22" }}>
              <Wifi className="h-8 w-8" style={{ color }} />
            </div>
          )}
          <h1 className="font-display text-2xl font-bold text-white">{welcome}</h1>
          <p className="text-sm text-slate-400 mt-1">{data.structure_name}</p>
          <p className="text-xs font-mono text-slate-600 mt-0.5">{data.ssid}</p>
          {cfg.info_text && <p className="text-xs text-slate-500 mt-3">{cfg.info_text}</p>}

          {result?.result === "accept" ? (
            <div data-testid="guest-success" className="mt-8 rounded-xl bg-emerald-500/10 ring-1 ring-emerald-500/30 p-6">
              <CheckCircle2 className="h-10 w-10 text-emerald-400 mx-auto" />
              <p className="text-emerald-300 font-semibold mt-2">{t("guest.success")}</p>
              {result.guest_name && <p className="text-xs text-slate-400 mt-1">{result.guest_name} · {result.profile}</p>}
              {isRealHotspot && (
                <p data-testid="guest-connecting" className="text-xs text-slate-400 mt-3 flex items-center justify-center gap-2">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Connessione alla rete in corso…
                </p>
              )}
            </div>
          ) : (
            <div className="mt-8">
              <div className="text-xs text-slate-500 mb-2">{t("guest.enter_code")}</div>
              <input
                data-testid="guest-code-input"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                onKeyDown={(e) => e.key === "Enter" && connect()}
                placeholder="XXXXXX"
                className="w-full rounded-xl bg-black/40 border border-white/10 px-3 py-4 text-center font-mono text-2xl tracking-[0.4em] text-white focus:outline-none focus:ring-2"
                style={{ caretColor: color }}
              />
              {result?.result === "reject" && (
                <div data-testid="guest-rejected" className="mt-3 flex items-center justify-center gap-1.5 text-sm text-rose-400">
                  <XCircle className="h-4 w-4" /> {result.reason || t("guest.rejected")}
                </div>
              )}
              <button
                data-testid="guest-connect-btn"
                onClick={connect}
                disabled={loading}
                className="mt-4 w-full rounded-xl py-3.5 text-sm font-semibold text-white flex items-center justify-center gap-2 transition-opacity disabled:opacity-60"
                style={{ background: color }}
              >
                {loading && <Loader2 className="h-4 w-4 animate-spin" />} {t("guest.connect")}
              </button>
            </div>
          )}
          <p className="text-[10px] uppercase tracking-widest text-slate-600 mt-8">{t("guest.powered")}</p>
        </div>
      </div>
    </div>
  );
}
